import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, Check, FileText, Filter, Plus, Search, Truck, X } from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { NewMoveForm, type NewMoveFormData } from '../components/moves/NewMoveForm';
import { CrewAssignmentModal } from '../components/jobs/CrewAssignmentModal';
import { TruckAssignmentModal } from '../components/jobs/TruckAssignmentModal';
import type { AssignedCrewMember, AssignedTruck } from '../hooks/useJobs';
import { MoveCard } from '../components/ui/MoveCard';
import {
  OrgUser,
  Quote,
  QUOTE_STATUS_CLASSES,
  QUOTE_STATUS_LABEL,
  JOB_STATUS_CLASSES,
  JOB_STATUS_LABEL,
} from '../types/admin';

// ── Types ───────────────────────────────────────────────────────────────────

interface DispatchItem {
  id: string;
  type: 'quote' | 'job';
  customerCompanyName: string;
  quoteNumber?: string;
  status?: string;
  jobStatus?: string;
  createdAt?: string;
  movingDate?: string;
  estimateVisitDate?: string;
  createdByName?: string;
  // Job-specific fields for crew/truck assignment
  assignedCrew?: AssignedCrewMember[];
  assignedTrucks?: AssignedTruck[];
  crewSize?: number;
  truckQuantity?: number;
  driverQuantity?: number;
  quoteType?: 'commercial' | 'residential';
}

interface DispatchResponse {
  success: boolean;
  data: DispatchItem[];
  counts: {
    quotesPending: number;
    jobsPending: number;
  };
}

type FilterMode = 'all' | 'quotes' | 'jobs';

// ── Assign Sheet ─────────────────────────────────────────────────────────────

function AssignSheet({
  item,
  users,
  loadingUsers,
  onClose,
  onAssign,
}: {
  item: DispatchItem;
  users: OrgUser[];
  loadingUsers: boolean;
  onClose: () => void;
  onAssign: (userId: string) => void;
}) {
  const [assigning, setAssigning] = useState<string | null>(null);
  const isQuote = item.type === 'quote';
  const roleLabel = isQuote ? 'Estimator' : 'Mover';
  const filtered = users.filter(u =>
    u.status === 'active' && u.role === (isQuote ? 'estimator' : 'mover'),
  );
  // Also include admins and field leads as potential assignees
  const adminsAndLeads = users.filter(u => u.status === 'active' && (u.role === 'admin' || u.role === 'field_lead'));
  const assignable = [...filtered, ...adminsAndLeads];

  async function handlePick(uid: string) {
    setAssigning(uid);
    onAssign(uid);
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-sm md:w-full md:pb-6 max-h-[70vh] flex flex-col">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-midnight">
              Assign {isQuote ? 'Quote Lead' : 'Move Lead'}
            </h2>
            <p className="text-[13px] text-slate mt-0.5 truncate">{item.customerCompanyName}</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={18} /></button>
        </div>

        {loadingUsers ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : assignable.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-small text-slate">
              No active {roleLabel.toLowerCase()}s found in this organization.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5 overflow-y-auto -mx-1 px-1">
            {assignable.map(u => (
              <li key={u.uid}>
                <button
                  type="button"
                  onClick={() => handlePick(u.uid)}
                  disabled={assigning !== null}
                  className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl border border-stone
                             hover:border-plum/30 hover:bg-plum/5 active:bg-mist
                             disabled:opacity-50 transition-all duration-150"
                >
                  <div className="w-8 h-8 rounded-full bg-plum/10 flex items-center justify-center shrink-0 text-[13px] font-semibold text-plum">
                    {u.displayName?.charAt(0)?.toUpperCase() || u.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-midnight truncate">
                      {u.displayName || u.email}
                    </p>
                    <p className="text-[12px] text-slate truncate">
                      {u.role === 'admin' ? 'Admin' : u.role === 'field_lead' ? 'Field Lead' : roleLabel}
                      {u.displayName ? ` · ${u.email}` : ''}
                    </p>
                  </div>
                  {assigning === u.uid && (
                    <LoadingSpinner size="sm" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Dispatch uses the shared NewMoveForm component — no local form needed.

// ── Dispatch Filter/Sort ────────────────────────────────────────────────────

interface DispatchFilterState {
  typeCommercial: boolean;
  typeResidential: boolean;
  jobStatusScheduled: boolean;
  quoteStatusDraft: boolean;
  quoteStatusSent: boolean;
  quoteStatusResponseReceived: boolean;
}

const DEFAULT_DISPATCH_FILTERS: DispatchFilterState = {
  typeCommercial: true,
  typeResidential: true,
  jobStatusScheduled: true,
  quoteStatusDraft: true,
  quoteStatusSent: true,
  quoteStatusResponseReceived: true,
};

type DispatchSortField = 'movingDate' | 'estimateVisitDate' | 'name';

interface DispatchSortState {
  field: DispatchSortField;
  dir: 'asc' | 'desc';
}

const DISPATCH_SORT_OPTIONS: { field: DispatchSortField; dir: 'asc' | 'desc'; label: string }[] = [
  { field: 'estimateVisitDate', dir: 'asc', label: 'Estimate date (soonest)' },
  { field: 'estimateVisitDate', dir: 'desc', label: 'Estimate date (latest)' },
  { field: 'movingDate', dir: 'asc', label: 'Move date (soonest)' },
  { field: 'movingDate', dir: 'desc', label: 'Move date (latest)' },
  { field: 'name', dir: 'asc', label: 'Name A–Z' },
  { field: 'name', dir: 'desc', label: 'Name Z–A' },
];

function DispatchFilterSheet({
  filters,
  onApply,
  onClose,
  showTypeFilter,
}: {
  filters: DispatchFilterState;
  onApply: (f: DispatchFilterState) => void;
  onClose: () => void;
  showTypeFilter: boolean;
}) {
  const [local, setLocal] = useState<DispatchFilterState>({ ...filters });

  function toggle(key: keyof DispatchFilterState) {
    setLocal(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
      ${checked ? 'border-plum bg-plum' : 'border-stone'}`}>
      {checked && (
        <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2 text-white">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Filter Dispatch</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>

        {/* Type */}
        {showTypeFilter && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Type</p>
            <div className="flex flex-col gap-2 mb-5">
              <button type="button" onClick={() => toggle('typeCommercial')}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
                  ${local.typeCommercial ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <CheckboxIcon checked={local.typeCommercial} />
                <span className={`text-small font-medium ${local.typeCommercial ? 'text-plum' : 'text-midnight'}`}>Commercial</span>
              </button>
              <button type="button" onClick={() => toggle('typeResidential')}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
                  ${local.typeResidential ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
                <CheckboxIcon checked={local.typeResidential} />
                <span className={`text-small font-medium ${local.typeResidential ? 'text-plum' : 'text-midnight'}`}>Residential</span>
              </button>
            </div>
          </>
        )}

        {/* Job Status */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Job Status</p>
        <div className="flex flex-col gap-2 mb-5">
          <button type="button" onClick={() => toggle('jobStatusScheduled')}
            className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
              ${local.jobStatusScheduled ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <CheckboxIcon checked={local.jobStatusScheduled} />
            <span className={`text-small font-medium ${local.jobStatusScheduled ? 'text-plum' : 'text-midnight'}`}>Scheduled</span>
          </button>
        </div>

        {/* Quote Status */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Quote Status</p>
        <div className="flex flex-col gap-2 mb-5">
          <button type="button" onClick={() => toggle('quoteStatusDraft')}
            className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
              ${local.quoteStatusDraft ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <CheckboxIcon checked={local.quoteStatusDraft} />
            <span className={`text-small font-medium ${local.quoteStatusDraft ? 'text-plum' : 'text-midnight'}`}>Draft</span>
          </button>
          <button type="button" onClick={() => toggle('quoteStatusSent')}
            className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
              ${local.quoteStatusSent ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <CheckboxIcon checked={local.quoteStatusSent} />
            <span className={`text-small font-medium ${local.quoteStatusSent ? 'text-plum' : 'text-midnight'}`}>Sent</span>
          </button>
          <button type="button" onClick={() => toggle('quoteStatusResponseReceived')}
            className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left transition-all duration-150
              ${local.quoteStatusResponseReceived ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}>
            <CheckboxIcon checked={local.quoteStatusResponseReceived} />
            <span className={`text-small font-medium ${local.quoteStatusResponseReceived ? 'text-plum' : 'text-midnight'}`}>Response Received</span>
          </button>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => { onApply(DEFAULT_DISPATCH_FILTERS); onClose(); }}
            className="flex-1 h-12 rounded-xl border border-stone text-small font-semibold text-slate
                       hover:border-plum/40 hover:text-midnight active:bg-mist transition-colors duration-150">
            Reset
          </button>
          <button type="button" onClick={() => { onApply(local); onClose(); }}
            className="flex-1 h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function DispatchSortSheet({
  current,
  onSelect,
  onClose,
}: {
  current: DispatchSortState;
  onSelect: (s: DispatchSortState) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Sort</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"><X size={16} /></button>
        </div>
        <div className="flex flex-col gap-1.5">
          {DISPATCH_SORT_OPTIONS.map(opt => {
            const active = current.field === opt.field && current.dir === opt.dir;
            return (
              <button key={`${opt.field}-${opt.dir}`} type="button"
                onClick={() => { onSelect({ field: opt.field, dir: opt.dir }); onClose(); }}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl text-[13px] font-medium transition-colors
                  ${active ? 'bg-plum/10 text-plum' : 'text-midnight hover:bg-mist'}`}>
                {opt.label}
                {active && <Check size={15} className="ml-auto text-plum" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Dispatch Content (reusable, used by OperationsPage) ─────────────────────

export interface DispatchCounts {
  quotesPending: number;
  jobsPending: number;
}

export function DispatchContent({
  orgId,
  onCountsChange,
  onSidebarContent,
}: {
  orgId: string;
  onCountsChange?: (counts: DispatchCounts) => void;
  onSidebarContent?: (content: React.ReactNode) => void;
}) {
  const navigate = useNavigate();
  const { activeOrg, hasActiveSubscription, currentUser } = useUserProfile();
  const noSubscription = !hasActiveSubscription && !currentUser?.isPlatformAdmin;
  const leadsEnabled = activeOrg?.featureLeadPipeline ?? false;

  // Feature flag checks
  const commercialEnabled = activeOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = activeOrg?.featureResidentialMovingQuotes ?? false;
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  const [items, setItems] = useState<DispatchItem[]>([]);
  const [counts, setCounts] = useState<DispatchCounts>({ quotesPending: 0, jobsPending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [dispatchFilters, setDispatchFilters] = useState<DispatchFilterState>(DEFAULT_DISPATCH_FILTERS);
  const [dispatchSort, setDispatchSort] = useState<DispatchSortState>({ field: 'movingDate', dir: 'asc' });
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Org users for assignment (quotes only)
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignItem, setAssignItem] = useState<DispatchItem | null>(null);

  // Crew/Truck assignment modals (jobs only)
  const [crewAssignItem, setCrewAssignItem] = useState<DispatchItem | null>(null);
  const [truckAssignItem, setTruckAssignItem] = useState<DispatchItem | null>(null);

  // Debounce search
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchItems = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (debouncedSearch) qs.set('search', debouncedSearch);
      const data = await api.get<DispatchResponse>(`/dispatch/${orgId}?${qs.toString()}`);
      setItems(data.data);
      setCounts(data.counts);
      onCountsChange?.(data.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dispatch items');
    } finally {
      setLoading(false);
    }
  }, [orgId, debouncedSearch, onCountsChange]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Fetch org users when the assign sheet opens
  useEffect(() => {
    if (!assignItem || !orgId || orgUsers.length > 0) return;
    setLoadingUsers(true);
    api.get<{ success: boolean; data: OrgUser[] }>(`/org-admin/${orgId}/users`)
      .then(res => setOrgUsers(res.data))
      .catch(() => {/* ignore — sheet will show empty state */})
      .finally(() => setLoadingUsers(false));
  }, [assignItem, orgId, orgUsers.length]);

  // Apply local filter + sort
  const filteredItems = useMemo(() => {
    let result = filterMode === 'all'
      ? items
      : items.filter(i => i.type === (filterMode === 'quotes' ? 'quote' : 'job'));

    // Type filter
    if (bothTypesEnabled) {
      result = result.filter(i => {
        const isCommercial = i.quoteType !== 'residential';
        if (isCommercial && !dispatchFilters.typeCommercial) return false;
        if (!isCommercial && !dispatchFilters.typeResidential) return false;
        return true;
      });
    }

    // Job status filter
    result = result.filter(i => {
      if (i.type === 'job') {
        if (i.jobStatus === 'scheduled' && !dispatchFilters.jobStatusScheduled) return false;
        // Only filter scheduled for now; other job statuses pass through
      }
      return true;
    });

    // Quote status filter
    result = result.filter(i => {
      if (i.type === 'quote') {
        const qs = i.status;
        if (qs === 'draft' && !dispatchFilters.quoteStatusDraft) return false;
        if (qs === 'sent' && !dispatchFilters.quoteStatusSent) return false;
        if (qs === 'response_received' && !dispatchFilters.quoteStatusResponseReceived) return false;
      }
      return true;
    });

    // Sort
    result = [...result].sort((a, b) => {
      if (dispatchSort.field === 'movingDate' || dispatchSort.field === 'estimateVisitDate') {
        const dateA = (dispatchSort.field === 'estimateVisitDate' ? a.estimateVisitDate : a.movingDate) ?? '';
        const dateB = (dispatchSort.field === 'estimateVisitDate' ? b.estimateVisitDate : b.movingDate) ?? '';
        // Items without dates go to the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        const cmp = dateA.localeCompare(dateB);
        return dispatchSort.dir === 'asc' ? cmp : -cmp;
      } else {
        // name sort: use customerCompanyName, falling back to pointOfContact name
        const nameA = (a.customerCompanyName || '').toLowerCase();
        const nameB = (b.customerCompanyName || '').toLowerCase();
        const cmp = nameA.localeCompare(nameB);
        return dispatchSort.dir === 'asc' ? cmp : -cmp;
      }
    });

    return result;
  }, [items, filterMode, dispatchFilters, dispatchSort, bothTypesEnabled]);

  const isDefaultSort = dispatchSort.field === 'movingDate' && dispatchSort.dir === 'asc';
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (bothTypesEnabled && (!dispatchFilters.typeCommercial || !dispatchFilters.typeResidential)) n++;
    if (!dispatchFilters.jobStatusScheduled) n++;
    if (!dispatchFilters.quoteStatusDraft || !dispatchFilters.quoteStatusSent || !dispatchFilters.quoteStatusResponseReceived) n++;
    return n;
  }, [dispatchFilters, bothTypesEnabled]);

  const handleCreateMove = useCallback(async (data: NewMoveFormData): Promise<Quote> => {
    const result = await api.post<{ success: boolean; data: Quote }>(`/quotes/${orgId}`, data);
    setShowNewSheet(false);
    navigate(`/moves/${result.data.id}`);
    return result.data;
  }, [orgId, navigate]);

  async function handleAssign(userId: string) {
    if (!assignItem || !orgId) return;
    try {
      await api.patch(`/quotes/${orgId}/${assignItem.id}`, { quoteLeadUserId: userId });
      setAssignItem(null);
      // Refresh list — the assigned item should disappear
      fetchItems();
    } catch {
      // Keep sheet open on error so user can retry
    }
  }



  async function handleCrewSave(crew: AssignedCrewMember[]) {
    if (!crewAssignItem || !orgId) return;
    await api.patch(`/jobs/${orgId}/${crewAssignItem.id}/crew`, { assignedCrew: crew });
    setCrewAssignItem(null);
    fetchItems();
  }

  async function handleTruckSave(trucks: AssignedTruck[]) {
    if (!truckAssignItem || !orgId) return;
    await api.patch(`/jobs/${orgId}/${truckAssignItem.id}/trucks`, { assignedTrucks: trucks });
    setTruckAssignItem(null);
    fetchItems();
  }

  // Provide sidebar content for desktop two-column layout
  useEffect(() => {
    onSidebarContent?.(
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setFilterMode(prev => prev === 'quotes' ? 'all' : 'quotes')}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
            ${filterMode === 'quotes'
              ? 'border-plum bg-plum/5 shadow-brand-sm'
              : 'border-stone bg-white hover:border-plum/30'
            }`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${filterMode === 'quotes' ? 'bg-plum/15' : 'bg-stone/60'}`}>
            <FileText size={14} className={filterMode === 'quotes' ? 'text-plum' : 'text-slate'} />
          </div>
          <div className="min-w-0">
            <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{counts.quotesPending}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">Quotes</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setFilterMode(prev => prev === 'jobs' ? 'all' : 'jobs')}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
            ${filterMode === 'jobs'
              ? 'border-blue-400 bg-blue-50 shadow-brand-sm'
              : 'border-stone bg-white hover:border-blue-300'
            }`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${filterMode === 'jobs' ? 'bg-blue-100' : 'bg-stone/60'}`}>
            <Truck size={14} className={filterMode === 'jobs' ? 'text-blue-600' : 'text-slate'} />
          </div>
          <div className="min-w-0">
            <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{counts.jobsPending}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">Jobs</p>
          </div>
        </button>
      </div>,
    );
  }, [onSidebarContent, counts, filterMode]);

  return (
    <>
      {/* Search + Sort/Filter */}
      <div className="flex items-center gap-2 mb-4" data-tour="search-bar">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          <input
            type="text"
            placeholder="Search by company or quote #..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-white text-small text-midnight
                       placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowSortSheet(true)}
          className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border transition-colors duration-150
            ${!isDefaultSort
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:border-plum/30 hover:text-midnight'}`}
          aria-label="Sort"
        >
          <ArrowUpDown size={16} />
        </button>
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border transition-colors duration-150 relative
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:border-plum/30 hover:text-midnight'}`}
          aria-label="Filter"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-plum text-white text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* List */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">{error}</p>
        </div>
      )}

      {!loading && !error && filteredItems.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-2xl bg-plum/10 flex items-center justify-center">
            <FileText size={24} className="text-plum" />
          </div>
          <div>
            <p className="font-semibold text-small text-midnight">
              {debouncedSearch ? 'No results found' : 'All caught up!'}
            </p>
            <p className="text-[13px] text-slate mt-1">
              {debouncedSearch
                ? `No items match "${debouncedSearch}".`
                : 'No quotes or jobs are pending assignment right now.'}
            </p>
          </div>
        </div>
      )}

      {!loading && !error && filteredItems.length > 0 && (() => {
        let firstQuoteSeen = false;
        let firstJobSeen = false;
        return (
        <ul className="flex flex-col gap-2">
          {filteredItems.map(item => {
            let cardTourAttr: string | undefined;
            if (item.type === 'quote' && !firstQuoteSeen) {
              firstQuoteSeen = true;
              cardTourAttr = 'dispatch-card-quote';
            } else if (item.type === 'job' && !firstJobSeen) {
              firstJobSeen = true;
              cardTourAttr = 'dispatch-card-job';
            }

            const stage = item.type === 'quote' ? 'quote' as const : 'job' as const;
            const statusLabel = item.type === 'quote'
              ? (QUOTE_STATUS_LABEL[item.status as keyof typeof QUOTE_STATUS_LABEL] ?? item.status ?? 'Draft')
              : (JOB_STATUS_LABEL[item.jobStatus as keyof typeof JOB_STATUS_LABEL] ?? item.jobStatus ?? 'Scheduled');
            const statusClasses = item.type === 'quote'
              ? (QUOTE_STATUS_CLASSES[item.status as keyof typeof QUOTE_STATUS_CLASSES] ?? 'bg-stone text-slate')
              : (JOB_STATUS_CLASSES[item.jobStatus as keyof typeof JOB_STATUS_CLASSES] ?? 'bg-stone text-slate');

            // Adapt flat DispatchItem shape to what MoveCard helpers expect
            const mappedData = {
              ...item,
              type: item.quoteType ?? 'commercial',
              movingDate: item.movingDate ? { preferredDate: item.movingDate } : undefined,
              logisticsSummary: { crewSize: item.crewSize, truckQuantity: item.truckQuantity },
              quoteLeadDisplayName: item.createdByName,
            };

            return (
            <li key={`${item.type}-${item.id}`} data-tour={cardTourAttr}>
              <MoveCard
                data={mappedData}
                stage={stage}
                statusLabel={statusLabel}
                statusClasses={statusClasses}
                onClick={noSubscription ? undefined : () => navigate(`/moves/${item.id}`)}
                onAssignEstimator={item.type === 'quote' ? () => setAssignItem(item) : undefined}
                onAssignCrew={item.type === 'job' ? () => setCrewAssignItem(item) : undefined}
                onAssignTruck={item.type === 'job' ? () => setTruckAssignItem(item) : undefined}
                disabled={noSubscription}
              />
            </li>
            );
          })}
        </ul>
        );
      })()}

      {/* FAB — New Lead/Quote */}
      {hasActiveSubscription && (
        <button
          type="button"
          data-tour={leadsEnabled ? 'new-lead-fab' : 'new-quote-fab'}
          onClick={() => setShowNewSheet(true)}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+16px)] right-4
                     z-40 h-14 w-14 rounded-full bg-plum text-white shadow-brand-md
                     flex items-center justify-center
                     hover:bg-rose active:scale-95 transition-all duration-150"
          aria-label={leadsEnabled ? 'New lead' : 'New quote'}
        >
          <Plus size={26} />
        </button>
      )}

      {/* New move sheet (shared component — decides lead vs quote based on feature flag + estimate date) */}
      {showNewSheet && (
        <NewMoveForm
          onClose={() => setShowNewSheet(false)}
          onCreate={handleCreateMove}
          showTypeSelector={bothTypesEnabled}
          defaultType={residentialEnabled && !commercialEnabled ? 'residential' : 'commercial'}
          orgId={orgId}
        />
      )}

      {/* Filter sheet */}
      {showFilterSheet && (
        <DispatchFilterSheet
          filters={dispatchFilters}
          onApply={setDispatchFilters}
          onClose={() => setShowFilterSheet(false)}
          showTypeFilter={bothTypesEnabled}
        />
      )}

      {/* Sort sheet */}
      {showSortSheet && (
        <DispatchSortSheet
          current={dispatchSort}
          onSelect={setDispatchSort}
          onClose={() => setShowSortSheet(false)}
        />
      )}

      {/* Assign sheet (quotes only) */}
      {assignItem && (
        <AssignSheet
          item={assignItem}
          users={orgUsers.filter(u => u.status === 'active')}
          loadingUsers={loadingUsers}
          onClose={() => setAssignItem(null)}
          onAssign={handleAssign}
        />
      )}

      {/* Crew assignment modal (jobs) */}
      {crewAssignItem && (
        <CrewAssignmentModal
          orgId={orgId}
          jobId={crewAssignItem.id}
          moveDate={crewAssignItem.movingDate}
          crewNeeded={crewAssignItem.crewSize ?? 0}
          driverQuantity={crewAssignItem.driverQuantity ?? (crewAssignItem.truckQuantity ?? 0)}
          quoteType={crewAssignItem.quoteType ?? 'commercial'}
          currentCrew={crewAssignItem.assignedCrew ?? []}
          onSave={handleCrewSave}
          onClose={() => setCrewAssignItem(null)}
        />
      )}

      {/* Truck assignment modal (jobs) */}
      {truckAssignItem && (
        <TruckAssignmentModal
          orgId={orgId}
          jobId={truckAssignItem.id}
          moveDate={truckAssignItem.movingDate}
          trucksNeeded={truckAssignItem.truckQuantity ?? 0}
          currentTrucks={truckAssignItem.assignedTrucks ?? []}
          onSave={handleTruckSave}
          onClose={() => setTruckAssignItem(null)}
        />
      )}
    </>
  );
}

// ── Page wrapper (standalone route) ─────────────────────────────────────────

export function DispatchPage() {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;

  if (activeOrg?.featureDispatch === false) {
    return (
      <div className="px-4 pt-16 flex flex-col items-center text-center gap-3">
        <p className="text-small text-slate font-semibold">Dispatch is not enabled for this organization.</p>
        <button type="button" onClick={() => navigate('/')} className="text-small text-plum hover:underline">Go to Quotes</button>
      </div>
    );
  }

  if (!orgId) return null;

  return (
    <div className="px-4 pt-4 pb-24 md:px-6 max-w-3xl mx-auto">
      <div className="mb-4">
        <h1 className="font-display text-h1 text-midnight">Dispatch</h1>
        <p className="text-small text-slate mt-0.5">Quotes and jobs pending assignment</p>
      </div>
      <DispatchContent orgId={orgId} />
    </div>
  );
}
